import React, { useCallback, useMemo, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom';
import { useStore } from '../../store/store';
import { containerBackground, contentHeight, ItemTypes, pageBackground } from '../../constants/constants';
import { effectiveZoom } from '../../utils/zoom';
import { canvasLeftShift, pageHeight, pageWidth, sidebarWidth } from '../../constants/gridConfig';
import ItemWrapper from '../Container/ItemWrapper';
import { VList } from 'virtua';
import { isATitleBox } from '../../utils/boxes';
import _ from 'lodash';
import { useContainersToShow } from '../../hooks';
import { useOverlay } from '../../contexts/OverlayContext';
import { isWindows } from '../../utils/browser';
import {
  downloadWheelDiagnostic,
  installWheelScrollDiagnostic,
  isWheelDiagnosticEnabled,
} from '../../debug/wheelScrollDiagnostic';
import {
  computePageBreaks,
  coversSeamBelow,
  isFullPageContainer,
  PAGE_SHADOW,
  pageBreakHeight,
  SEAM_COVER_SHADOW,
} from '../../utils/pageBreaks';

const selector = ({
  configuration,
  landscape,
  boxes,
  columns,
  setFirstVisibleContainerId,
  zoomLevel,
  proposalList,
}) => {
  return {
    configuration,
    landscape,
    boxes,
    columns,
    setFirstVisibleContainerId,
    zoomLevel,
    proposalList,
  };
};

let vlistRef = null;
let columnIdToItemIndex = new Map();

// Ascenseur visible sous Windows (cf. .windows-scrollbar dans Middle.css).
const showScrollbar = isWindows();
const wheelDiagnosticEnabled = isWheelDiagnosticEnabled();

// Le scroll du document, vertical ET horizontal, vit dans le scroller de VList
// (className='no-scrollbar' plus bas), pas dans #pdf. Le mode main et la
// mesure de canScrollX (Middle.jsx) passent donc par lui.
export const getVListScrollOffset = () => vlistRef?.scrollOffset ?? 0;
export const scrollVListTo = (offset) => vlistRef?.scrollTo(offset);
// null si aucune VList n'est montée (vue liste, édition de masque).
export const getCanvasScroller = () => document.querySelector('#pdf .no-scrollbar');

// Item virtua élargi à son contenu quand la page déborde (fort zoom) : virtua
// pose `contain: layout` sur ses items, dont le débord ne remonte alors pas
// jusqu'au scroller — la page dépasserait sans scroll horizontal possible.
const CanvasItem = ({ style, children, ref }) => (
  <div ref={ref} style={{ ...style, width: 'max-content', minWidth: '100%' }}>
    {children}
  </div>
);

// onlyIfNotVisible (défaut false) → align 'nearest' : virtua ne scrolle pas si
// l'item est déjà entièrement visible, sinon scrolle juste assez pour le voir.
// false → align 'start' : amène toujours l'item en haut.
export const scrollToItem = (box, { onlyIfNotVisible = false } = {}) => {
  const itemIndex = columnIdToItemIndex.get(box.columnId);
  if (itemIndex == null) return;
  vlistRef?.scrollToIndex(itemIndex, {
    align: onlyIfNotVisible ? 'nearest' : 'start',
  });
};

export const scrollToBoxById = (boxId, boxes) => {
  const box = boxes.find((b) => b.id === boxId);
  if (!box) return;
  scrollToItem(box);
};

const InfinitePage = () => {
  const {
    boxes,
    configuration,
    columns,
    landscape,
    setFirstVisibleContainerId,
    zoomLevel,
    proposalList,
  } = useStore(selector);

  const { overlayProps } = useOverlay();
  const containersToShow = useContainersToShow()
  const hasInitializedRef = useRef(false);
  const listRef = useRef(null);

  // Exposer la ref pour scrollToItem
  useEffect(() => {
    vlistRef = listRef.current;
    return () => { vlistRef = null; };
  });

  // Diagnostic molette (?wheel_diag=1) — cf. src/debug/wheelScrollDiagnostic.js
  const diagnosticContextRef = useRef();
  diagnosticContextRef.current = () => ({
    zoomLevel,
    effectiveZoom: effectiveZoom(zoomLevel, landscape),
    landscape,
    showScrollbar,
  });
  const getDiagnosticContext = useCallback(() => diagnosticContextRef.current(), []);
  useEffect(() => {
    if (!wheelDiagnosticEnabled) return undefined;
    const scroller = getCanvasScroller();
    if (!scroller) return undefined;
    return installWheelScrollDiagnostic(scroller, getDiagnosticContext);
  }, [getDiagnosticContext]);

  // Créer des Maps pour les lookups O(1) au lieu de find() O(n)
  const boxesByColumnId = useMemo(() => {
    const map = new Map();
    boxes.forEach((b) => {
      if (b.columnId) {
        if (!map.has(b.columnId)) {
          map.set(b.columnId, []);
        }
        map.get(b.columnId).push(b);
      }
    });
    return map;
  }, [boxes]);

  const columnsById = useMemo(() => {
    return new Map(columns.map((c) => [c.id, c]));
  }, [columns]);

  const findBoxeByColumnId = useCallback((columnId) => {
    const boxesForColumn = boxesByColumnId.get(columnId);
    return boxesForColumn?.[0];
  }, [boxesByColumnId])

  const formatContainer = useCallback(() => {
    const titleIndices = {
      [ItemTypes.TITLE]: 0,
      [ItemTypes.TITLE_2]: 1,
      [ItemTypes.TITLE_3]: 2,
      [ItemTypes.TITLE_4]: 3,
    };
    let skip = false;
    let titleLevel;

    return containersToShow.map((container) => {
      const box = findBoxeByColumnId(container.columnsIds[0]);

      const column = columnsById.get(container.columnsIds[0]);
      if (!column) return { type: 'container', id: container.id };
      const { drawMode, id } = column;
      const isPage = isFullPageContainer({ container, column, landscape });

      if (drawMode) {
        const boxesForColumn = boxesByColumnId.get(id) || [];
        const boxTitleCollapsed = boxesForColumn.find((b) => isATitleBox(b.type) && b.collapsed === true);
        const boxTitleNotCollapsed = boxesForColumn.find((b) => isATitleBox(b.type) && !b.collapsed);

        if (boxTitleCollapsed) {
          if (titleIndices[boxTitleCollapsed?.type] > titleLevel) return { type: 'title_container_hidden', id: container.id };
          skip = true;
          titleLevel = titleIndices[boxTitleCollapsed?.type];
          return { type: 'container', id: container.id, titleCollapsed: true, isTitleLevel1: titleIndices[boxTitleCollapsed?.type] === 0, isPage };
        }
        if (boxTitleNotCollapsed) {
          skip = false;
          return { type: 'container', id: container.id, isTitleLevel1: titleIndices[boxTitleNotCollapsed?.type] === 0, isPage };
        }
      };

      if (isATitleBox(box?.type) && box.collapsed === true) {
        if (titleIndices[box?.type] > titleLevel) return { type: 'title_container_hidden', id: container.id };
        skip = true;
        titleLevel = titleIndices[box?.type];
        return { type: 'container', id: container.id, titleCollapsed: true, isTitleLevel1: titleIndices[box?.type] === 0, isPage };
      }
      if (isATitleBox(box?.type) && titleIndices[box?.type] <= titleLevel) {
        skip = false;
      }
      if (skip) {
        return { type: 'title_container_hidden', id: container.id };
      }
      if (box?.type !== ItemTypes.SECTION_LINK) {
        return { type: 'container', id: container.id, isTitleLevel1: titleIndices[box?.type] === 0, isPage };
      }
      if (box?.type === ItemTypes.SECTION_LINK) {
        return null;
      }

      return { type: 'container', id: container.id, isTitleLevel1: titleIndices[box?.type] === 0, isPage };
    }).filter((container) => !!container)
  }, [containersToShow, findBoxeByColumnId, boxesByColumnId, columnsById, landscape])


  const items = useMemo(() => {
    return [
      ..._.compact(formatContainer()),
      { type: 'infinite-page-drop', id: 0 },
    ]
  }, [formatContainer]);

  // Map columnId -> index dans items pour scrollToItem en O(1)
  useEffect(() => {
    const map = new Map();
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type === 'container' || item.type === 'title_container_hidden') {
        const container = containersToShow.find((c) => c.id === item.id);
        if (container) {
          for (const colId of container.columnsIds) {
            map.set(colId, i);
          }
        }
      }
    }
    columnIdToItemIndex = map;
    return () => { columnIdToItemIndex = new Map(); };
  }, [items, containersToShow]);

  // Définir le premier container visible au chargement initial (une seule fois)
  useEffect(() => {
    if (hasInitializedRef.current) return;
    const firstContainer = items.find((item) => item && item.type === 'container');
    if (firstContainer) {
      hasInitializedRef.current = true;
      setFirstVisibleContainerId(firstContainer.id);
    }
  }, [items, setFirstVisibleContainerId]);

  const onScroll = useMemo(
    () =>
      _.debounce(() => {
        if (!listRef.current) return
        const startOffset = listRef.current.scrollOffset
        const endOffset = startOffset + listRef.current.viewportSize
        let startIndex = listRef.current.findItemIndex(startOffset)
        let endIndex = listRef.current.findItemIndex(endOffset)
        if (startIndex === 0) startIndex = 1
        if (endIndex === items.length - 1) endIndex -= 1
        const visibleContainer = startIndex === endIndex ? [items[startIndex]] : items.slice(startIndex, endIndex + 1)
        const visibleContainerId = visibleContainer.find((item) => item.type === 'container' && item.isTitleLevel1)?.id ||
          visibleContainer.find((item) => item.type === 'container')?.id
        setFirstVisibleContainerId(visibleContainerId)
      }, 100),
    [setFirstVisibleContainerId, items]
  )

  const pageBreaks = useMemo(() => computePageBreaks(items), [items]);

  const zoomPercent = effectiveZoom(zoomLevel, landscape);
  const documentWidth = landscape ? pageHeight : pageWidth;
  // Les panels latéraux sont fixes, par-dessus le canevas : le scroller va du
  // bord gauche de #pdf (le panel gauche, redimensionnable, le recouvre) au
  // panel droit s'il est déplié — sa barre de défilement longe donc le gris,
  // contre le panel. Chaque item centre la page entre les panels dépliés
  // (+6px à gauche, cf. canvasLeftShift) ; replier le panel droit ne la
  // déplace pas, comme avant. Pas de panels en vue liste.
  const rightInset = proposalList || overlayProps.right.collapsed ? 0 : sidebarWidth;
  const leftInset = proposalList ? 0 : canvasLeftShift + sidebarWidth;
  const rightPadding = proposalList ? 0 : sidebarWidth - rightInset;

  return (
    <div className="page" style={{ width: '100%' }} data-role={pageBackground}>
      {/* Portail : un ancêtre crée un contexte d'empilement, les panneaux
          latéraux passeraient au-dessus du bouton malgré son zIndex. */}
      {wheelDiagnosticEnabled && createPortal(
        <button
          type="button"
          style={{ position: 'fixed', left: '50%', bottom: 16, transform: 'translateX(-50%)', zIndex: 2147483647 }}
          onClick={() => downloadWheelDiagnostic(getCanvasScroller(), getDiagnosticContext)}
        >
          Télécharger le diagnostic molette
        </button>,
        document.body
      )}
      {/* Un seul scroller, dans les deux axes, sur toute la zone grise : la
          molette y est native partout (plus de relais depuis #pdf) et il
          porte aussi le débord horizontal au fort zoom. Le zoom CSS est posé
          sur chaque item, pas au-dessus de VList : virtua mesure et défile en
          px réels. */}
      <VList
        ref={listRef}
        style={{
          position: 'relative',
          width: `calc(100% - ${rightInset}px)`,
          height: contentHeight,
          overflowX: 'auto',
          color: configuration.baseFontColor?.style,
        }}
        className={showScrollbar ? 'no-scrollbar windows-scrollbar' : 'no-scrollbar'}
        item={CanvasItem}
        onScroll={onScroll}
      >
            {items.map((item, index) => {
              const breaks = pageBreaks[index];
              return (
                // Ligne pleine largeur (clic = fond du canevas) ; la colonne
                // zoomée, à la largeur du document, est centrée par les marges
                // auto, qui tombent à 0 quand elle déborde (fort zoom) : le
                // débord part à droite, atteignable par le scroll horizontal.
                <div key={`${item.type}-${item.id}`} style={{
                  display: "flex",
                  paddingLeft: leftInset,
                  paddingRight: rightPadding,
                }}
                  data-role={pageBackground}
                >
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  flexShrink: 0,
                  width: documentWidth,
                  marginLeft: "auto",
                  marginRight: "auto",
                  zoom: `${zoomPercent}%`,
                }}
                  data-role={pageBackground}
                  data-parent={containerBackground}
                >
                  {/* Bandes « saut de page » : transparentes, elles laissent voir
                      le gris du canevas (#pdf) et l'ombre des pages voisines. Un
                      clic dedans désélectionne, comme sur le reste du fond. */}
                  {breaks.before && (
                    <div
                      style={{
                        height: pageBreakHeight({
                          lineSpacing: configuration.lineSpacing,
                          isDocumentTop: index === 0,
                        }),
                      }}
                      data-role={pageBackground}
                    />
                  )}
                  <div style={{
                    display: "flex",
                    backgroundColor: breaks.onBackground ? undefined : "white",
                    // Débord blanc d'1px : couvre la jointure avec l'item
                    // suivant (cf. coversSeamBelow).
                    boxShadow: [
                      item.isPage && PAGE_SHADOW,
                      coversSeamBelow(pageBreaks, index) && SEAM_COVER_SHADOW,
                    ].filter(Boolean).join(', ') || undefined,
                    flexDirection: "column",
                    width: landscape ? pageHeight : pageWidth,
                    marginLeft: "auto",
                    marginRight: "auto",
                    position: 'relative',
                  }}>
                    <ItemWrapper
                      type={item.type}
                      titleCollapsed={item.titleCollapsed}
                      index={index}
                      itemId={item.id}
                      itemLength={items.length}
                      previousItemId={items[index - 1]?.id}
                      isPage={!!item.isPage}
                    />
                  </div>
                  {breaks.after && (
                    <div
                      style={{ height: pageBreakHeight({ lineSpacing: configuration.lineSpacing }) }}
                      data-role={pageBackground}
                    />
                  )}
                </div>
                </div>
              )
            })}
      </VList>
    </div>
  )
}

export default InfinitePage;
